import { gsap, Back, Bounce } from 'gsap';
import { Helper } from '../../../core/utils/Helper';
import { SpinePlayer } from '../../../core/displayobject/SpinePlayer';
import { Beheaded } from '../../Beheaded';
import { MathHelper } from '../../../core/utils/MathHelper';
import { SoundConfig } from '../../config/sound/SoundConfig';
import { GameSetting } from '../../../core/setting/GameSetting';
import { SoundManager } from '../../config/sound/SoundManager';

export enum SWtype {
    SW1 = 'sw_sad_indicator',
    SW2 = 'sw_laugh_indicator',
    SW3 = 'sw_angry_indicator',
}

//this is the SW that always remains on the INDICATOR BAR on the left
export class SoulWildIndicator extends PIXI.Container {
    public ID: number;

    private swDude: SpinePlayer;

    private ringSpine: SpinePlayer;
    private ringSpineFXSp: SpinePlayer[];
    public mainSpineFX: SpinePlayer;

    public isEnabled: boolean = false;
    private isEnabledSound: boolean = false;
    public isActivated: boolean = false;
    public _progressBarBase: SpinePlayer;
    private _progressBarFill: PIXI.Sprite;

    public collectedSouls: number = 0;
    public collectedSoulsThreshold: number;

    private tlUpdate: gsap.core.Timeline;
    private unlockIndex: number = 0;

    public get ThresholdReached(): boolean {
        return this.collectedSouls == this.collectedSoulsThreshold;
    }

    public get soulsMissing(): number {
        return this.collectedSoulsThreshold - this.collectedSouls;
    }

    public get soulsCollected(): number {
        return this.collectedSouls;
    }

    public get unlockedIndex(): number {
        return this.unlockIndex;
    }

    public get willBeUnlocked(): boolean {
        return this.unlockIndex == this.ringSpineFXSp.length;
    }

    public get indicatorUsed(): boolean {
        return !!this.unlockIndex;
    }

    private get SWSpinePath(): string {
        const skin = this.ID === 0 ? 'green' : this.ID === 2 ? 'red' : 'purple';
        return `soul_wild_${skin}`;
    }

    constructor(ID: number, _threshold: number) {
        super();

        this.ID = ID;
        this.name = `SW_INDICATOR_${ID}`;
        this.collectedSoulsThreshold = _threshold;
        this.init();
    }

    init(): void {
        this.initGraphics();
    }

    private get fetchSpineIndicator(): string {
        return ['SW_Green', 'SW_Purple', 'SW_Red'][this.ID];
    }

    initGraphics(): void {
        // indicator ring
        this.ringSpine = new SpinePlayer('independent_ring', true);
        this.addChild(this.ringSpine);

        this.createIndicators();

        // indicator SW
        this.mainSpineFX = new SpinePlayer(this.fetchSpineIndicator, true);
        this.mainSpineFX.play({ animation: 'enabled_to_default', showOnEnd: true });
        this.addChild(this.mainSpineFX);
        this.mainSpineFX.scale.set(0.95);

        // indicator bar
        this._progressBarBase = new SpinePlayer(`spine/sw_bar.json`);
        this._progressBarBase.play({ animation: 'disabled', showOnEnd: true }); // disabled/enabled/glow_loop
        this._progressBarBase.alpha = 0;

        this._progressBarBase.position.set(0, 60);
        this._progressBarBase.scale.set(1, 1);
        this.addChild(this._progressBarBase);

        this._progressBarFill = new PIXI.Sprite(Beheaded.assetLoader.getTextures('swBarFill')[0]);
        this._progressBarFill.anchor.set(0, 0.5);
        this._progressBarFill.position.set(-37, 60);
        this._progressBarFill.scale.set(0, 1);

        this.addChild(this._progressBarFill);

        this.swDude = this.addChild(new SpinePlayer(this.SWSpinePath));
    }

    private get maximumIndicators(): number {
        return [1, 3, 5][this.ID];
    }

    createIndicators(): void {
        this.ringSpineFXSp = [];
        new SpinePlayer('independent_lock'); // active active_loop appear inactive unlock

        for (let i = 0; i < this.maximumIndicators; i++) {
            const indiCator = new SpinePlayer('independent_lock');

            this.ringSpineFXSp.push(indiCator);
            indiCator.position = this.indiCatorPositions(i);
            indiCator.angle = this.indicatorRotations(i);
            indiCator.scale.set(1.1);
        }

        this.ringSpineFXSp.forEach((ind) => {
            this.addChild(ind);
            ind.play({ animation: 'inactive', showOnEnd: true });
        });
    }

    private indiCatorPositions(indx: number): PIXI.Point {
        switch (this.ID) {
            case 1:
                return [new PIXI.Point(0, -50), new PIXI.Point(-35, 37), new PIXI.Point(35, 37)][indx];
            case 2:
                return [
                    new PIXI.Point(0, -50),
                    new PIXI.Point(-50, -10), // 315
                    new PIXI.Point(-35, 37),
                    new PIXI.Point(35, 37),
                    new PIXI.Point(55, -10),
                ][indx];
        }
        return new PIXI.Point(0, -50); // 0
    }

    private indicatorRotations(indx: number): number {
        switch (this.ID) {
            case 1:
                return [
                    0, // 1
                    220, // 2
                    140, // 3
                ][indx];
            case 2:
                return [0, 270, 220, 140, 90][indx];
        }
        return 0;
    }

    public unlockNext(
        unclockAll: boolean = false,
        isIntro: boolean = false,
        playRestoreSound: boolean = false
    ): gsap.core.Timeline {
        const target = this.ringSpineFXSp[this.unlockIndex];
        const tl = gsap.timeline();

        if (unclockAll || !target) {
            this.isEnabled = true;

            const unlockedDelay: number = 0; // unclockAll ? 0 : this.ringSpineFXSp[0].fetchDuration("unlock");

            this.ringSpineFXSp.forEach((ind, indx) => {
                tl.add(() => {
                    ind.play({ animation: 'unlock' });
                }, (indx * unlockedDelay) / 2);
            });

            const nowDelay: number = tl.duration();

            tl.add(() => {
                this.enable(isIntro, playRestoreSound);
            }, nowDelay);
            tl.add(() => {
                this.ringSpine.play({ animation: 'unlock' });
            }, nowDelay);
            this.unlockIndex = this.maximumIndicators;
        } else {
            tl.add(
                target?.play({
                    animation: 'active',
                    showOnEnd: true,
                    onComplete: () => {
                        target.play({ animation: 'active_loop', loop: true });
                    },
                }),
                0
            );
            this.unlockIndex++;
            if (this.willBeUnlocked)
                tl.add(() => {
                    this.unlockNext();
                }, 0);
        }
        return tl;
    }

    public resetRing(isBoosted: boolean = false, isintro: boolean): void {
        if (this.isEnabled) {
            this.ringSpine.play({
                animation: 'disk_appear',
                showOnEnd: true,
                onComplete: () => {
                    const tl = gsap.timeline();
                    this.ringSpineFXSp.forEach((ind, indx) => {
                        tl.add(ind.play({ animation: !indx && isBoosted ? 'active' : 'appear', showOnEnd: true }), indx * 0.1);
                    });
                },
            });

            // This makes sure that only one instance is played.
            if (!isintro) {
                Beheaded.soundPlayer.stopSound(SoundConfig.SW_DISABLED);
                Beheaded.soundPlayer.playSound(SoundConfig.SW_DISABLED);
            }
            this.disable();
        } else if (this.unlockIndex) {
            for (let k = isBoosted ? 1 : 0; k < this.unlockIndex; k++) {
                this.ringSpineFXSp[k].play({ animation: 'inactive', showOnEnd: true });
            }
        }

        this.unlockIndex = isBoosted ? 1 : 0;
    }

    charge(): gsap.core.Timeline {
        return this.mainSpineFX.play({
            animation: 'enabled_H_gather',
            showOnEnd: true,
            onComplete: () => {
                if (!this.ThresholdReached) this.mainSpineFX.play({ animation: 'enabled_loop', showOnEnd: true, loop: true });
            },
        });
    }

    enable(introPlay: boolean = false, playRestoreSound: boolean = false): gsap.core.Timeline {
        this.isEnabled = true;
        let tl;
        if (!introPlay) {
            tl = this._progressBarBase.play({
                animation: 'enabled',
                showOnEnd: true,
                onStart: () => {
                    if (!this.isEnabledSound) {
                        if (
                            (!Beheaded.model.gameData.wasFeatureBuy && Beheaded.model.gameData.nextLevel === 5) ||
                            (Beheaded.model.gameData.wasFeatureBuy && Beheaded.model.gameData.nextLevel !== 5) ||
                            GameSetting.isBoostedBet
                        ) {
                            this.isEnabledSound = true;
                        }
                    }
                },
                onComplete: () => {
                    this._progressBarBase.play({
                        animation: 'glow_loop',
                        loop: true,
                        duration: this._progressBarBase.fetchDuration('glow_loop') * 2,
                    });
                },
            });
            tl.fromTo(this._progressBarBase, { alpha: 0 }, { alpha: 1, duration: tl.duration() }, 0);
        } else {
            tl = gsap.timeline();
        }

        tl.add(() => {
            const appearDurr = 2;

            const tlSW = this.swDude.play({ animation: 'Win', showOnEnd: true, duration: appearDurr });
            if (!introPlay) {
                !playRestoreSound && SoundManager.playSoundEffect(SoundConfig.SW_ENABLED[this.ID]);
            }
            tlSW.fromTo(this.swDude, { alpha: 0, duration: appearDurr / 2 }, { alpha: 1, duration: appearDurr }, 0);
            tlSW.fromTo(
                this.swDude.scale,
                { x: 0.75, y: 0.75, duration: appearDurr / 2 },
                { x: 1, y: 1, duration: appearDurr / 2 },
                0
            );
            tlSW.to(this.swDude, { alpha: 0, duration: appearDurr / 2 }, appearDurr / 2);
        });

        return this.mainSpineFX.play({
            animation: 'enabled',
            showOnEnd: true,
            onComplete: () => {
                this.mainSpineFX.play({ animation: 'enabled_loop', showOnEnd: true, loop: true });
            },
        });
    }

    forceReset() {
        this.isEnabled = false;
        this.isEnabledSound = false;
        this.isActivated = false;
        this.updateBar(0);
        this._progressBarBase.play({ animation: 'disabled', showOnEnd: true });
        for (let k = 0; k < this.unlockIndex; k++) {
            this.ringSpineFXSp[k].play({ animation: 'inactive', showOnEnd: true });
        }
        this.unlockIndex = 0;
    }

    showProgressionBar(): void {
        this._progressBarBase.play({ animation: 'glow_loop', showOnEnd: true });
        gsap.to(this._progressBarBase, { alpha: 1, duration: 0.25 });
        gsap.to(this._progressBarFill, { alpha: 1, duration: 0.25 });
    }
    hideProgressionBar(): void {
        this._progressBarBase.play({ animation: 'disabled', showOnEnd: true });
        gsap.to(this._progressBarFill, { alpha: 0, duration: 0.25 });
    }

    disable(): gsap.core.Timeline {
        this.isEnabled = false;
        this.isEnabledSound = false;
        this.isActivated = false;

        this.updateBar(0);

        const tl = this.mainSpineFX.play({
            animation: 'enabled_to_default',
            showOnEnd: true,
            onComplete: () => {
                this._progressBarBase.play({ animation: 'disabled', showOnEnd: true });
            },
        });

        tl.to(this._progressBarBase, { alpha: 0, duration: tl.duration() }, 0);

        return tl;
    }

    activate(): gsap.core.Timeline {
        this.isActivated = true;
        return this.mainSpineFX.play({
            animation: 'activated',
            showOnEnd: true,
            onComplete: () => {
                this.mainSpineFX.play({ animation: 'activated_loop', loop: true });
            },
        });
    }

    deactivate(): gsap.core.Timeline {
        const tl = gsap.timeline();
        if (this.isEnabled) {
            if (this.isActivated) {
                tl.add(
                    this.mainSpineFX.play({
                        animation: 'activated_reversed',
                        showOnEnd: true,
                        onComplete: () => this.mainSpineFX.play({ animation: 'enabled_loop', showOnEnd: true, loop: true }),
                    }),
                    0
                );
                this.isActivated = false;
            }
            this.updateBar(0);
        }
        return tl;
    }

    collectionProgress(progressIndex: number): gsap.core.Timeline {
        this.tlUpdate = Helper.setTimeline(
            this.tlUpdate,
            gsap.timeline({
                onComplete: () => {
                    if (this.ThresholdReached) this.activate();
                },
            })
        );

        this.tlUpdate.to(this._progressBarFill.scale, {
            x: progressIndex / this.collectedSoulsThreshold,
            duration: 0.175,
        });

        if (!this.isEnabled) {
            this.tlUpdate.to(this._progressBarFill, { duration: 0.175, alpha: progressIndex ? 1 : 0 }, 0);
            this.tlUpdate.to(this._progressBarBase, { duration: 0.175, alpha: progressIndex ? 1 : 0 }, 0);
        } else {
            this._progressBarFill.alpha = 1;
        }

        return this.tlUpdate;
    }

    updateBar(value: number): void {
        if (!value) {
            this.collectedSouls = 0;
            this.collectionProgress(this.collectedSouls);
        } else {
            if (this.ThresholdReached) return;
            this.collectedSouls = Math.min(this.collectedSoulsThreshold, this.collectedSouls + value);
            this.collectionProgress(this.collectedSouls);
            this.charge();
        }
    }
}
